import { InformationCircleIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';

interface FieldRecommendationProps {
  message: string;
  cyTag?: string;
  className?: string;
  id?: string;
}

/**
 * [MCV-1066] A non-blocking nudge to fill in a field that is optional in the
 * schema but valuable in practice.
 *
 * This is deliberately NOT a validation error. The vault accepts very lax
 * submissions on purpose -- a provider part-way through credentialing often
 * genuinely does not have a date yet -- so a recommendation must never gate
 * submit, never set form state, and never read as a failure. It is advice.
 *
 * Colour: the semantic `info` tone rather than `warning`. `warning` reads as
 * "something is wrong", and per the contrast table in styles.css the solid
 * warning tone is 2.61:1 on its own tint -- below AA for small text, where only
 * `text-warning-950` clears it. `info` is the honest signal and passes.
 *
 * Not `role="alert"`: unlike FieldError this is not urgent and must not
 * interrupt a screen reader mid-task. Callers wire it up with
 * `aria-describedby` so it is read as part of the field's description instead.
 */
export const FieldRecommendation = ({
  message,
  cyTag,
  className,
  id,
}: FieldRecommendationProps) => (
  <p
    id={id}
    data-cy={cyTag}
    className={classNames(
      'flex items-center gap-1 body-text text-info text-left',
      className ?? 'mt-1.5'
    )}
  >
    <InformationCircleIcon className="size-4 shrink-0" aria-hidden="true" />
    <span>{message}</span>
  </p>
);
